import styled from 'styled-components';

import CcFill from 'Assets/mtm/cc-fill.svg';
import Cc from 'Assets/mtm/cc.svg';
import MtmBg from 'Assets/mtm/mtm-bg.png';
import Pause from 'Assets/mtm/pause.svg';
import Play from 'Assets/mtm/play.svg';
import SpeakerOff from 'Assets/mtm/speaker-off.svg';
import SpeakerOn from 'Assets/mtm/speaker-on.svg';
import triangle from 'Assets/mtm/triangle.svg';
import { breakpoint } from 'DesignLibrary/global';
import { SkewHoverKeyframes } from 'DesignLibrary/keyframes';

interface MomentPageLayoutProps {
  topic?: string;
}

export const StorybookRuntimeContainer = styled.div`
  max-width: 1082px;
  width: 100%;
  margin: 0 auto;
  min-height: calc(-376px + 100vh);
  display: flex;
  align-items: center;
`;

export const MomentPageLayoutWrapper = styled.div<MomentPageLayoutProps>`
  .inner-wrapper {
    max-width: 100%;
    background: linear-gradient(to top, #fbf7f3 0%, #ffffff 100%);
  }
`;

export const StorybookRuntimeWrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sizing-lg);
  border-radius: 24px;
  box-shadow: 0px 2px 8px 0px #2f2f5229;
  background: #ffffff;
  padding: 16px;
  width: 100%;

  video {
    border-radius: 16px;
  }

  ${breakpoint.DESKTOP} {
    display: grid;
    grid-template-columns: 2fr 1fr;
  }

  .dreamcast-scope {
    .moment.container {
      height: 100%;
      padding-top: 0;
    }

    .overlay {
      position: absolute;
      overflow: hidden;
    }

    .question-text {
      font-size: 1.5rem;
      font-weight: 300;
      line-height: 1.3;
      text-align: center;
      margin: 0 auto 1.5rem;
      flex: 0 0 100%;
    }

    .choice {
      display: inline-flex;

      button {
        font-size: 1rem;
        height: 48px;
        border: 2px solid #bebeca;
        background: white;

        &:hover {
          border-color: var(--colors-alex-blue);
        }

        &:active,
        &:focus {
          background: var(--colors-alex-blue);
          border-color: var(--colors-alex-blue);
          color: white;
          transform: scale(1);
        }
      }
    }

    .conversation {
      position: relative;
      height: 100%;
      border-radius: 1rem;

      ${breakpoint.DESKTOP} {
        min-height: 382px;
      }

      .uicontainer {
        position: absolute;
        bottom: 0.5rem;
        left: 1rem;
        top: auto;
        width: 96%;
        display: flex;
        justify-content: space-between;

        + div {
          height: 100%;
        }

        #skip {
          display: none;
        }

        #cc {
          background: url(${Cc}) no-repeat center center;
          background-size: 24px 24px;
          order: 3;

          &.selected {
            background-image: url(${CcFill});
          }

          &:hover {
            background-color: rgba(255, 255, 255, 0.5);
          }
        }

        #playpause {
          background: url(${Pause}) no-repeat center center;
          background-size: 18px 18px;

          &.selected {
            background-image: url(${Play});
          }

          &:hover {
            background-color: rgba(255, 255, 255, 0.5);
          }
        }

        #mute {
          margin-right: 0.5rem;
          margin-left: auto;
          background: url(${SpeakerOn}) no-repeat center center;
          background-size: 18px 18px;
          order: 2;

          &.selected {
            background-image: url(${SpeakerOff});
          }

          &:hover {
            background-color: rgba(255, 255, 255, 0.5);
          }
        }

        .ui-button,
        .ui-button.selected {
          border: 0;
          color: transparent;
        }
      }

      .closedcaption-container {
        position: absolute;
        padding: 0.5rem 1rem;
        background: rgba(47, 47, 82, 0.68);
        border-radius: 0.5rem;
        left: 50%;
        transform: translateX(-50%);
        width: auto;

        .closedcaption-text {
          font-size: 1rem;
          font-weight: 300;
          padding: 0;
          line-height: 1.4;
        }
      }

      video-js {
        margin-top: 0;

        .vjs-big-play-button {
          display: block;
          background: rgba(47, 47, 82, 0.8);
          border: 0;
          border-radius: 10rem;
          height: 100px;
          width: 100px;
          left: 70%;
          top: 44%;

          &:hover {
            background: rgba(47, 47, 82, 1);
            animation: ${SkewHoverKeyframes} 2000ms ease-in-out infinite alternate;
            transform: scale(1.04);
          }

          .vjs-icon-placeholder:before {
            content: '';
            display: block;
            width: 60px;
            height: 60px;
            background: url(${triangle}) no-repeat center center;
            background-size: 60px 60px;
            top: 22%;
            left: 25%;
          }
        }

        &.vjs-playing,
        &.vjs-has-started {
          .vjs-big-play-button {
            display: none;
          }
        }

        .vjs-control-bar {
          display: flex !important;
          visibility: hidden;

          .vjs-time-tooltip {
            display: none;
          }

          .vjs-load-progress {
            display: none;
          }

          .vjs-progress-control {
            display: flex;
            visibility: visible;
            position: absolute;
            width: 100%;
            left: 0;
            margin: 0;
            bottom: 2rem;
            right: 0;

            .vjs-progress-holder {
              margin: 0;
              height: 5px;
              background: rgba(255, 255, 255, 0.25);
            }

            .vjs-play-progress {
              &:before {
                display: none;
              }
            }
          }
        }
      }

      .row.single-column-half-width {
        display: flex;
        height: 100%;
        background: var(--colors-alex-blue10) url(${MtmBg}) no-repeat center center;
        border-radius: 1rem;

        [class^='col-'] {
          float: none;
          width: 100%;
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          align-content: center;
          align-items: center;
          justify-content: center;
          gap: 0 1.5rem;
          padding: 5rem 0;

          &:empty {
            display: none;
          }

          ${breakpoint.DESKTOP} {
            padding: 0.5rem;
          }
        }
      }
    }
  }
`;

export const MomentSidebarContainer = styled.div`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RatingsContainer = styled.div`
  padding-top: var(--sizing-lg);
  padding-bottom: var(--sizing-xxl);
  text-align: center;
  max-width: 415px;
  margin: 0 auto;
`;

export const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
